import { useEffect, useState } from 'react';

import { RIDE_STATUS_COLORS, RIDE_STATUS_LABELS } from './invoiceLabels';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { FEATURES } from '../../constants';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { downloadFile } from '../../lib/downloadFile';
import { formatAbsoluteDateTime, formatCurrency, formatShortDateTime } from '../../lib/formatters';
import { useExtranetInvoiceDetail } from '../../services/api/hooks/useExtranetInvoiceDetail';
import type { ExtranetInvoiceOutput, ExtranetInvoiceRide } from '../../types/extranet-api.types';
import { IconDownload, IconX } from '../incidents/icons';

const toEuros = (cents: number): number => cents / 100;

interface InvoiceDetailDrawerProps {
  invoiceId: string | null;
  onClose: () => void;
}

const MONTHS = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const formatPeriod = (from: string, to: string) => {
  const f = new Date(from);
  const t = new Date(to);
  const fYear = f.getUTCFullYear();
  const tYear = t.getUTCFullYear();
  const fMonth = f.getUTCMonth();
  const tMonth = t.getUTCMonth();
  const sameYear = fYear === tYear;
  const sameMonth = sameYear && fMonth === tMonth;
  if (sameMonth) return `${MONTHS[fMonth]} ${fYear}`;
  if (sameYear) return `${MONTHS[fMonth]}–${MONTHS[tMonth]} ${fYear}`;
  return `${MONTHS[fMonth]} ${fYear} – ${MONTHS[tMonth]} ${tYear}`;
};

export const InvoiceDetailDrawer = ({ invoiceId, onClose }: InvoiceDetailDrawerProps) => {
  const query = useExtranetInvoiceDetail(invoiceId);

  useEffect(() => {
    if (!invoiceId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [invoiceId, onClose]);

  if (!invoiceId) return null;

  const invoice = query.data;

  return (
    <div className="fixed inset-0 z-40">
      <div
        onClick={onClose}
        aria-hidden
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-drawer-title"
        className="absolute top-0 right-0 h-full w-full sm:w-[720px] bg-white border-l border-zinc-200 drawer-panel-in flex flex-col"
      >
        {query.isLoading || !invoice ? (
          <DrawerSkeleton onClose={onClose} />
        ) : (
          <DrawerContent invoice={invoice} onClose={onClose} />
        )}
      </div>
    </div>
  );
};

const DrawerContent = ({ invoice, onClose }: { invoice: ExtranetInvoiceOutput; onClose: () => void }) => {
  const { can } = useAccount();
  const canDownloadPdf = can('invoicing');
  const completedRides = invoice.rides.filter(r => r.status === 'completed').length;
  const cancelledRides = invoice.rides.filter(r => r.status === 'cancelled').length;
  const noShowRides = invoice.rides.filter(r => r.status === 'no_show').length;

  return (
    <>
      <header className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <h2 id="invoice-drawer-title" className="font-mono tabular-nums text-lg text-zinc-900 truncate">
            {invoice.id}
          </h2>
          <InvoiceStatusBadge displayStatus={invoice.status === 'validated' ? 'validated' : 'pending'} />
        </div>
        <div className="flex items-center gap-2">
          {canDownloadPdf ? <InvoiceDownloadButton invoiceId={invoice.id} kind="pdf" /> : null}
          {FEATURES.invoiceRidesExport ? <InvoiceDownloadButton invoiceId={invoice.id} kind="rides" /> : null}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <Section title="Facture">
          <KeyValueGrid
            items={[
              { label: 'Entité', value: invoice.entityName },
              { label: 'Réf. BUPO', value: invoice.siteRef, mono: true },
              { label: 'Contrat', value: invoice.contractRef, mono: true },
              {
                label: 'Période',
                value: formatPeriod(invoice.periodFrom, invoice.periodTo),
              },
              { label: 'Date d’émission', value: formatAbsoluteDateTime(invoice.issuedAt), mono: true },
              { label: 'Courses facturées', value: String(invoice.rides.length), mono: true },
              { label: 'TVA', value: `${invoice.vatRate} %`, mono: true },
            ]}
          />
        </Section>

        <Section title="Montants">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5">
            <div className="flex items-center justify-between">
              <dt className="text-sm text-zinc-500">Montant HT</dt>
              <dd className="font-mono tabular-nums text-sm text-zinc-900">
                {formatCurrency(toEuros(invoice.amountExcludingTax))}
              </dd>
            </div>
            <div className="flex items-center justify-between">
              <dt className="text-sm text-zinc-500">TVA ({invoice.vatRate} %)</dt>
              <dd className="font-mono tabular-nums text-sm text-zinc-700">
                {formatCurrency(toEuros(invoice.amountIncludingTax - invoice.amountExcludingTax))}
              </dd>
            </div>
            <div className="col-span-2 border-t border-zinc-100 pt-2.5 flex items-center justify-between">
              <dt className="text-sm font-medium text-zinc-900">Montant TTC</dt>
              <dd className="font-mono tabular-nums text-base font-semibold text-zinc-900">
                {formatCurrency(toEuros(invoice.amountIncludingTax))}
              </dd>
            </div>
          </dl>
        </Section>

        <Section title={`Listing des courses (${invoice.rides.length})`}>
          <div className="flex items-center gap-4 text-xs text-zinc-500 mb-3">
            <RideStat label="Réalisées" count={completedRides} dot="bg-emerald-500" />
            <RideStat label="Annulées" count={cancelledRides} dot="bg-zinc-400" />
            <RideStat label="No-show" count={noShowRides} dot="bg-red-500" />
          </div>
          <RidesTable rides={invoice.rides} />
        </Section>

        <div className="h-8" />
      </div>
    </>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="px-6 py-5 border-b border-zinc-100 last:border-b-0">
    <h3 className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 mb-3">{title}</h3>
    <div className="flex flex-col gap-3">{children}</div>
  </section>
);

const KeyValueGrid = ({ items }: { items: { label: string; value: string; mono?: boolean }[] }) => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5">
    {items.map(item => (
      <div key={item.label} className="flex flex-col gap-0.5 min-w-0">
        <dt className="text-xs text-zinc-500">{item.label}</dt>
        <dd
          className={['text-sm text-zinc-900 truncate', item.mono ? 'font-mono tabular-nums' : ''].join(' ')}
          title={item.value}
        >
          {item.value}
        </dd>
      </div>
    ))}
  </dl>
);

const RideStat = ({ label, count, dot }: { label: string; count: number; dot: string }) => (
  <span className="flex items-center gap-1.5">
    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden />
    <span className="font-mono tabular-nums text-zinc-700">{count}</span>
    <span className="text-zinc-500">{label}</span>
  </span>
);

const RidesTable = ({ rides }: { rides: ExtranetInvoiceRide[] }) => (
  <div className="border border-zinc-200 rounded-md overflow-hidden">
    <div className="max-h-[420px] overflow-y-auto">
      <table className="min-w-full text-xs">
        <thead className="sticky top-0 bg-zinc-50/95 backdrop-blur-sm">
          <tr className="border-b border-zinc-200">
            <RideTh>N° Course</RideTh>
            <RideTh>GALAC&apos;SI</RideTh>
            <RideTh>Date</RideTh>
            <RideTh>Trajet</RideTh>
            <RideTh align="right">Km</RideTh>
            <RideTh align="right">Attente</RideTh>
            <RideTh>Statut</RideTh>
            <RideTh align="right">Montant</RideTh>
          </tr>
        </thead>
        <tbody>
          {rides.map(ride => {
            const status = RIDE_STATUS_COLORS[ride.status];
            return (
              <tr key={ride.id} className="border-b border-zinc-100 last:border-b-0">
                <td className="px-3 py-2 font-mono tabular-nums text-zinc-900 whitespace-nowrap">
                  {ride.id.slice(-10)}
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-zinc-500 whitespace-nowrap">{ride.externalRef}</td>
                <td
                  className="px-3 py-2 font-mono tabular-nums text-zinc-500 whitespace-nowrap"
                  title={formatAbsoluteDateTime(ride.pickupAt)}
                >
                  {formatShortDateTime(ride.pickupAt)}
                </td>
                <td className="px-3 py-2 text-zinc-700 max-w-[180px]">
                  <span className="block truncate" title={`${ride.origin} → ${ride.destination}`}>
                    {ride.origin} → {ride.destination}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-zinc-700 text-right whitespace-nowrap">
                  {ride.distanceKm.toFixed(1)}
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-zinc-500 text-right whitespace-nowrap">
                  {ride.waitMinutes} min
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-flex items-center gap-1.5 ${status.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} aria-hidden />
                    {RIDE_STATUS_LABELS[ride.status]}
                  </span>
                </td>
                <td className="px-3 py-2 font-mono tabular-nums text-zinc-900 text-right whitespace-nowrap">
                  {ride.amount > 0 ? formatCurrency(toEuros(ride.amount)) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

const RideTh = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'} whitespace-nowrap`}
  >
    {children}
  </th>
);

const InvoiceDownloadButton = ({ invoiceId, kind }: { invoiceId: string; kind: 'pdf' | 'rides' }) => {
  const toast = useToast();
  const [pending, setPending] = useState(false);
  const config =
    kind === 'pdf'
      ? {
          path: `/v1/invoices/${invoiceId}/pdf`,
          filename: `${invoiceId}.pdf`,
          label: 'PDF',
        }
      : {
          path: `/v1/extranet/invoices/${invoiceId}/rides.xlsx`,
          filename: `${invoiceId}-courses.xlsx`,
          label: 'Courses .xlsx',
        };
  const handleClick = async () => {
    setPending(true);
    try {
      await downloadFile(config.path, config.filename);
    } catch (err) {
      const { title, description } = toUserErrorMessage(err, 'Téléchargement échoué');
      toast.error(title, description);
    } finally {
      setPending(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={pending}
      className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 hover:border-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
    >
      <IconDownload width={12} height={12} />
      <span>{pending ? 'Téléchargement…' : config.label}</span>
    </button>
  );
};

const DrawerSkeleton = ({ onClose }: { onClose: () => void }) => (
  <>
    <header className="sticky top-0 z-10 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="h-5 w-32 rounded-sm skeleton-pulse" />
        <div className="h-5 w-16 rounded-md skeleton-pulse" />
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
      >
        <IconX width={16} height={16} />
      </button>
    </header>
    <div className="flex-1 overflow-y-auto">
      {[1, 2, 3].map(i => (
        <div key={i} className="px-6 py-5 border-b border-zinc-100 flex flex-col gap-3">
          <div className="h-3 w-24 rounded-sm skeleton-pulse" />
          <div className="h-4 w-full rounded-sm skeleton-pulse" />
          <div className="h-4 w-4/5 rounded-sm skeleton-pulse" />
        </div>
      ))}
    </div>
  </>
);
